import type { BadgeTone } from '@personachat/ui';

import type { components } from '../api/schema';

/**
 * Libellés et tons partagés : les mêmes pastilles partout (cahier des charges §4).
 */
export type TypeCompte = components['schemas']['UserType'];
export type RaisonMouvement = components['schemas']['LedgerReason'];

export const TYPES_COMPTE: TypeCompte[] = ['NORMAL', 'GUEST', 'CHALLENGE3'];

export const LIBELLE_TYPE: Record<TypeCompte | 'ADMIN', string> = {
  NORMAL: 'Normal',
  GUEST: 'Invité',
  CHALLENGE3: 'Immersion 3',
  ADMIN: 'Admin',
};

export const TON_TYPE: Record<TypeCompte | 'ADMIN', BadgeTone> = {
  NORMAL: 'neutral',
  GUEST: 'gold',
  CHALLENGE3: 'terracotta',
  ADMIN: 'success',
};

/** Ce que change un passage vers ce type (dialogue de changement de type). */
export const EFFET_TYPE: Record<TypeCompte, string> = {
  NORMAL: 'Accès complet : modules d’événement (Discoveur, Immersion 3) retirés, Coach Ikigai débloqué.',
  GUEST: 'Invitée du Discoveur : le module Discoveur est ouvert ; l’application n’affiche plus que lui.',
  CHALLENGE3: 'Immersion 3 : le module de l’événement est ouvert ; l’application n’affiche plus que lui.',
};

export interface StatutCompte {
  libelle: string;
  ton: BadgeTone;
}

export function statutCompte(u: { isActive: boolean; deletedAt?: string | null }): StatutCompte {
  if (u.deletedAt) return { libelle: 'Supprimé', ton: 'danger' };
  return u.isActive ? { libelle: 'Actif', ton: 'success' } : { libelle: 'Désactivé', ton: 'neutral' };
}

export const RAISONS: RaisonMouvement[] = [
  'SIGNUP_GRANT',
  'PACK_PURCHASE',
  'PLAN_RENEWAL',
  'ADMIN_ADJUST',
  'IMAGE_GEN',
  'VIDEO_GEN',
  'REFUND',
  'ROLLBACK',
  'EXPIRATION',
];

export const LIBELLE_RAISON: Record<RaisonMouvement, string> = {
  SIGNUP_GRANT: 'Offerts à l’inscription',
  PACK_PURCHASE: 'Achat de pack',
  PLAN_RENEWAL: 'Abonnement',
  ADMIN_ADJUST: 'Ajustement admin',
  IMAGE_GEN: 'Génération d’image',
  VIDEO_GEN: 'Génération vidéo',
  REFUND: 'Remboursement',
  ROLLBACK: 'Annulation',
  EXPIRATION: 'Crédits perdus en fin de période',
};

/** Libellé précis d'un mouvement, selon sa raison et sa référence. */
export function libelleMouvement(m: { reason: RaisonMouvement; refType?: string | null }): string {
  const ref = m.refType ?? '';
  if (m.reason === 'ADMIN_ADJUST') {
    if (ref === 'quota-conversion') return 'Conversion des quotas V1';
    if (ref === 'admin-bulk') return 'Ajout groupé';
    if (ref === 'rattrapage-credits') return 'Rattrapage des crédits';
  }
  if (m.reason === 'SIGNUP_GRANT' && ref === 'passage-normal') return 'Offerts au passage en compte complet';
  if (m.reason === 'PLAN_RENEWAL' && ref === 'renouvellement') return 'Renouvellement de l’accès';
  if (m.reason === 'IMAGE_GEN') {
    if (ref.includes('moodboard')) return 'Moodboard';
    if (ref.includes('logo')) return 'Logo';
    if (ref.includes('palette')) return 'Palette';
    return 'Image';
  }
  if (m.reason === 'REFUND') return ref === 'payment_refund' ? 'Paiement remboursé (crédits retirés)' : 'Remboursement (génération échouée)';
  return LIBELLE_RAISON[m.reason];
}

/** Cause regroupée d'une génération échouée (champ failureCause du journal des crédits). */
export const LIBELLE_CAUSE_ECHEC: Record<string, string> = {
  moderation: 'Refusée par la modération du fournisseur',
  delai: 'Délai dépassé',
  quota: 'Quota ou limite du fournisseur atteint',
  demande: 'Demande invalide',
  fournisseur: 'Fournisseur indisponible ou en erreur',
  autre: 'Autre erreur',
};

/** Remboursement d'une génération échouée (et non d'un paiement remboursé). */
export function estEchecGeneration(m: { reason: RaisonMouvement; refType?: string | null }): boolean {
  return m.reason === 'REFUND' && m.refType !== 'payment_refund';
}

export function statutCle(k: { isActive: boolean; expiresAt?: string | null; usedCount: number; maxUses: number }): StatutCompte {
  if (!k.isActive) return { libelle: 'Désactivée', ton: 'neutral' };
  if (k.expiresAt && new Date(k.expiresAt).getTime() < Date.now()) return { libelle: 'Expirée', ton: 'danger' };
  if (k.usedCount >= k.maxUses) return { libelle: 'Pleine', ton: 'terracotta' };
  return { libelle: 'Active', ton: 'success' };
}

// ---------------------------------------------------------------------------
// Cycle d'accès (règles du 17/10/2026)
// ---------------------------------------------------------------------------

export type StatutAcces = components['schemas']['AccessStatus'];
export type PeriodeAcces = NonNullable<components['schemas']['AccessPeriod']>;

export const STATUT_ACCES: Record<StatutAcces, StatutCompte> = {
  ACTIVE: { libelle: 'Accès en cours', ton: 'success' },
  EXTENDED: { libelle: 'Prolongé 3 mois', ton: 'gold' },
  ENDED: { libelle: 'Accès terminé', ton: 'danger' },
  PAUSED: { libelle: 'En pause', ton: 'neutral' },
  NONE: { libelle: 'Sans échéance', ton: 'neutral' },
};

export const LIBELLE_PERIODE: Record<PeriodeAcces, string> = {
  FIRST_YEAR: 'Première année',
  EXTENSION: 'Prolongation (crédits restants)',
  RENEWAL: 'Renouvellement',
  ENDED: 'Terminé',
};

export const STATUT_RENOUVELLEMENT: Record<'PENDING' | 'ACTIVE' | 'ENDED' | 'CANCELED', StatutCompte> = {
  PENDING: { libelle: 'Payé d’avance', ton: 'gold' },
  ACTIVE: { libelle: 'En cours', ton: 'success' },
  ENDED: { libelle: 'Terminé', ton: 'neutral' },
  CANCELED: { libelle: 'Annulé (remboursé)', ton: 'neutral' },
};

/** Durée d'un pack : renouvellement de l'accès ou recharge. */
export function libelleDureePack(accessMonths: number | null | undefined): string {
  return accessMonths ? `Renouvellement ${accessMonths} mois` : 'Recharge';
}

// ---------------------------------------------------------------------------
// Ventes et événements (lot 2)
// ---------------------------------------------------------------------------

export const STATUT_ABONNEMENT: Record<string, StatutCompte> = {
  ACTIVE: { libelle: 'Active', ton: 'success' },
  PAST_DUE: { libelle: 'Paiement en retard', ton: 'danger' },
  INCOMPLETE: { libelle: 'Incomplète', ton: 'neutral' },
  CANCELED: { libelle: 'Résiliée', ton: 'neutral' },
};

export const STATUT_PAIEMENT: Record<string, StatutCompte> = {
  SUCCEEDED: { libelle: 'Payé', ton: 'success' },
  PENDING: { libelle: 'En attente', ton: 'neutral' },
  FAILED: { libelle: 'Échoué', ton: 'danger' },
  REFUNDED: { libelle: 'Remboursé', ton: 'terracotta' },
};

export const STATUT_WEBHOOK: Record<string, StatutCompte> = {
  processed: { libelle: 'Traité', ton: 'success' },
  ignored: { libelle: 'Ignoré', ton: 'neutral' },
  duplicate: { libelle: 'Doublon', ton: 'neutral' },
  failed: { libelle: 'En échec', ton: 'danger' },
  invalid_signature: { libelle: 'Signature invalide', ton: 'danger' },
};

export const STATUT_EVENEMENT: Record<'a_venir' | 'en_cours' | 'termine', StatutCompte> = {
  a_venir: { libelle: 'À venir', ton: 'gold' },
  en_cours: { libelle: 'En cours', ton: 'success' },
  termine: { libelle: 'Terminé', ton: 'neutral' },
};

export const LIBELLE_PERIODICITE: Record<'MONTH' | 'YEAR', string> = { MONTH: 'Mensuel', YEAR: 'Annuel' };
export const PAR_PERIODE: Record<'MONTH' | 'YEAR', string> = { MONTH: 'par mois', YEAR: 'par an' };

/** Lien vers le tableau de bord Stripe du bon mode (« test/ » en mode test) ; null sans Stripe. */
export function lienStripe(mode: 'live' | 'test' | null | undefined, chemin: string): string | null {
  if (!mode) return null;
  return `https://dashboard.stripe.com/${mode === 'test' ? 'test/' : ''}${chemin}`;
}
